// Account → Pricing (#38): the company's pricing MODELS (Neil, 10 Oct 2026: "allow them to create
// multiple pricing models; when we create a pack, ask them to select their pricing model"). Each
// model: one £/kWp per size band (optionally itemised), extras, assumptions and "roof left
// clear". One can be the default (pre-selected when ordering); our standard prices are always
// there, read-only. Owners and admins edit; members see them read-only. Each order keeps a copy of
// the model chosen for it (100-api functions/buildings), so a change applies to later packs.
import { createContext, useContext, useEffect, useState, type FormEvent } from "react";
import { ArrowLeft, Copy, Eye, Lock, Pencil, Plus, RotateCcw, Trash2 } from "lucide-react";
import { useApi, type Pricing, type PricingModel, type PricingResponse } from "../api";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { Field, Segmented, Toggle } from "../components/controls";
import "../coverage.css";
import "../pricing.css";
import "../widgets.css";

const PARTS: [keyof NonNullable<Pricing["bands"][number]["parts"]>, string][] = [
  ["panels", "Panels"],
  ["inverters", "Inverters"],
  ["mounting", "Mounting"],
  ["electrical", "Electrical"],
  ["labour", "Labour"],
  ["margin", "Margin"],
];
const MAX_BANDS = 8;
const ACCESS_LABELS: Record<string, string> = { scaffold: "Scaffolding", mewp: "Access platform", fixed: "Fixed price", none: "None" };
const STANDARD = "standard"; // our standard prices (100-api common/pricing_models.STANDARD_ID)

/** The model on screen: one of the company's (or a new one), or our standard prices. */
type Selected = Pick<PricingModel, "id" | "name" | "pricing" | "isDefault" | "versionId" | "version"> & {
  isNew?: boolean;
  description?: string;
  updatedAt?: string | null;
  updatedBy?: string | null;
};
const longDate = (iso: string) => new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
const money = (n: number) => `£${n.toLocaleString("en-GB", { maximumFractionDigits: 2 })}`;

// A read-only model (our standard prices, or a member who can't edit) shows its values as plain text,
// not as input boxes: nothing on screen looks editable that isn't (Neil, 14 Oct 2026)
const ReadOnlyCtx = createContext(false);

/** A number input that keeps what's typed and reports a number (or NaN while it's not one); plain text when read-only. */
function Num({
  id,
  value,
  onChange,
  step = 1,
  suffix,
  prefix,
}: {
  id: string;
  value: number;
  onChange: (n: number) => void;
  step?: number;
  suffix?: string;
  prefix?: string;
}) {
  const readOnly = useContext(ReadOnlyCtx);
  if (readOnly) {
    return (
      <span className="pr-value" id={id}>
        {prefix}
        {Number.isFinite(value) ? value.toLocaleString("en-GB", { maximumFractionDigits: 2 }) : "—"}
        {suffix ? ` ${suffix}` : ""}
      </span>
    );
  }
  return (
    <span className="pr-num">
      {prefix && <span className="pr-affix">{prefix}</span>}
      <input
        id={id}
        className="input"
        type="number"
        inputMode="decimal"
        step={step}
        value={Number.isFinite(value) ? value : ""}
        onChange={(e) => onChange(e.target.value === "" ? NaN : Number(e.target.value))}
      />
      {suffix && <span className="pr-affix">{suffix}</span>}
    </span>
  );
}

export function PricingPage() {
  const api = useApi();
  const [data, setData] = useState<PricingResponse | null>(null);
  // The model being edited: one of the company's, a new one, or our standard prices (read-only)
  const [sel, setSel] = useState<Selected | null>(null);
  const [message, setMessage] = useState<{ tone: "success" | "danger"; text: string } | null>(null);
  const [saving, setSaving] = useState(false);
  // The page (Neil, 14 Oct 2026): a grid of the models first; edit, duplicate or add opens the editor
  const [view, setView] = useState<"list" | "edit">("list");
  // The model whose delete is being confirmed (from its card or the editor)
  const [toDelete, setToDelete] = useState<{ id: string; name: string } | null>(null);

  /** Open a model (or our standard prices, read-only) in the editor. */
  const open = (r: PricingResponse, id: string) => {
    const m = r.models.find((x) => x.id === id);
    setSel(
      m
        ? { ...m, pricing: structuredClone(m.pricing) }
        : {
            id: STANDARD,
            name: r.standard.name,
            description: r.standard.description,
            pricing: structuredClone(r.standard.pricing),
            isDefault: !r.models.some((x) => x.isDefault),
          },
    );
    setMessage(null);
    setView("edit");
  };
  /** Back to the grid of models. */
  const toList = () => {
    setView("list");
    setSel(null);
  };

  useEffect(() => {
    api
      .getPricing()
      .then(setData)
      .catch((e: Error) => setMessage({ tone: "danger", text: e.message }));
  }, [api]);

  const isStandard = sel?.id === STANDARD;
  const readOnly = !data?.canEdit || isStandard;
  const draft = sel?.pricing ?? null;
  const update = (patch: Partial<Pricing>) => setSel((s) => (s ? { ...s, pricing: { ...s.pricing, ...patch } } : s));
  const setBand = (i: number, patch: Partial<Pricing["bands"][number]>) =>
    setSel((s) => (s ? { ...s, pricing: { ...s.pricing, bands: s.pricing.bands.map((b, j) => (j === i ? { ...b, ...patch } : b)) } } : s));

  // Switching to itemised: start each band's parts so they add up to its current £/kWp
  const setItemised = (on: boolean) =>
    update({
      itemised: on,
      bands: (sel?.pricing.bands ?? []).map((b) =>
        on && !b.parts ? { ...b, parts: { panels: 0, inverters: 0, mounting: 0, electrical: 0, labour: b.perKwp, margin: 0 } } : b,
      ),
    });

  /**
   * A new model (Neil, 10 Oct 2026: "add new, delete, edit existing, and duplicate one to then edit"):
   * "new" starts from our standard prices; "copy" duplicates a model (ours included: the one on screen,
   * or the card's) to edit.
   */
  const startNew = (from: "new" | "copy", source?: Selected) => {
    if (!data) return;
    const copy = from === "copy" && (source ?? sel);
    const taken = new Set(data.models.map((m) => m.name.toLowerCase()));
    // A name that isn't taken: "X (copy)", then "X (copy 2)", …
    let name = copy ? `${copy.name} (copy)` : "New pricing model";
    for (let n = 2; taken.has(name.toLowerCase()); n++) name = copy ? `${copy.name} (copy ${n})` : `New pricing model ${n}`;
    setSel({
      id: "",
      isNew: true,
      name: name.slice(0, 60),
      description: copy ? copy.description ?? "" : "",
      pricing: structuredClone(copy ? copy.pricing : data.standard.pricing),
      isDefault: data.models.length === 0,
    });
    setView("edit");
    setMessage(copy ? { tone: "success", text: `Duplicated "${copy.name}". Change what you need, then save.` } : null);
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!sel || readOnly) return;
    setSaving(true);
    setMessage(null);
    try {
      const r = sel.isNew
        ? await api.createPricingModel(sel.name, sel.pricing, sel.isDefault, sel.description ?? "")
        : await api.updatePricingModel(sel.id, { name: sel.name, description: sel.description ?? "", pricing: sel.pricing, isDefault: sel.isDefault });
      setData(r);
      toList();
      setMessage({ tone: "success", text: `Saved "${sel.name}". You can choose it for reports you order from now on.` });
    } catch (err) {
      setMessage({ tone: "danger", text: err instanceof Error ? err.message : "Couldn't save. Please try again." });
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!toDelete) return;
    setSaving(true);
    try {
      const r = await api.deletePricingModel(toDelete.id);
      setData(r);
      toList();
      setMessage({ tone: "success", text: `Deleted "${toDelete.name}". Reports already ordered with it keep its prices.` });
    } catch (err) {
      setMessage({ tone: "danger", text: err instanceof Error ? err.message : "Couldn't delete. Please try again." });
    } finally {
      setSaving(false);
      setToDelete(null);
    }
  };

  return (
    <div className="page">
      <div className="page-head">
        <h1>Pricing models</h1>
        <p>
          Your own prices and assumptions, so reports match how you quote. When you order a property you choose which model it's calculated on; it keeps
          those prices until you recalculate it on another model, or on a newer version of the same one, from its page.
        </p>
      </div>

      {message && <div className={`flash flash-${message.tone}`}>{message.text}</div>}
      {data && !data.canEdit && <div className="flash flash-info">Only the account owner or an admin can change pricing.</div>}

      {!data ? (
        !message && <p className="muted">Loading…</p>
      ) : view === "list" || !sel || !draft ? (
        /* The models, three across: name, description and when made, then edit, duplicate and delete */
        <div className="pm-grid">
          {data.models.map((m) => (
            <article key={m.id} className={`card pm-card${m.isDefault ? " is-default" : ""}`}>
              <div className="pm-card-head">
                <h2>
                  <button type="button" className="pm-title" onClick={() => open(data, m.id)}>
                    {m.name}
                  </button>
                </h2>
                <span className="pm-version muted">v{m.version ?? 1}</span>
                {m.isDefault && <span className="pricing-default">Default</span>}
              </div>
              <p className={`pm-desc${m.description ? "" : " muted"}`}>{m.description || "No description yet."}</p>
              <div className="pm-card-foot">
                <span className="pm-date muted">
                  {m.createdAt ? `Created ${longDate(m.createdAt)}` : m.updatedAt ? `Last changed ${longDate(m.updatedAt)}` : ""}
                </span>
                <span className="pm-icons">
                  <button type="button" className="pm-icon" aria-label={`${data.canEdit ? "Edit" : "View"} ${m.name}`} title={data.canEdit ? "Edit" : "View"} onClick={() => open(data, m.id)}>
                    {data.canEdit ? <Pencil size={15} aria-hidden="true" /> : <Eye size={15} aria-hidden="true" />}
                  </button>
                  {data.canEdit && data.models.length < 20 && (
                    <button type="button" className="pm-icon" aria-label={`Duplicate ${m.name}`} title="Duplicate" onClick={() => startNew("copy", { ...m })}>
                      <Copy size={15} aria-hidden="true" />
                    </button>
                  )}
                  {data.canEdit && (
                    <button type="button" className="pm-icon is-danger" aria-label={`Delete ${m.name}`} title="Delete" onClick={() => setToDelete({ id: m.id, name: m.name })}>
                      <Trash2 size={15} aria-hidden="true" />
                    </button>
                  )}
                </span>
              </div>
            </article>
          ))}
          {/* Our standard prices: always there, read-only (view or duplicate) */}
          <article className={`card pm-card is-standard${data.models.some((m) => m.isDefault) ? "" : " is-default"}`}>
            <div className="pm-card-head">
              <h2>
                <button type="button" className="pm-title" onClick={() => open(data, STANDARD)}>
                  {data.standard.name}
                </button>
              </h2>
              <span className="pricing-readonly">
                <Lock size={11} aria-hidden="true" /> Read-only
              </span>
              {!data.models.some((m) => m.isDefault) && <span className="pricing-default">Default</span>}
            </div>
            <p className="pm-desc">{data.standard.description || "Used when you haven't made a pricing model of your own."}</p>
            <div className="pm-card-foot">
              <span className="pm-date muted">Always available</span>
              <span className="pm-icons">
                <button type="button" className="pm-icon" aria-label={`View ${data.standard.name}`} title="View" onClick={() => open(data, STANDARD)}>
                  <Eye size={15} aria-hidden="true" />
                </button>
                {data.canEdit && data.models.length < 20 && (
                  <button
                    type="button"
                    className="pm-icon"
                    aria-label={`Duplicate ${data.standard.name}`}
                    title="Duplicate"
                    onClick={() => startNew("copy", { id: STANDARD, name: data.standard.name, pricing: data.standard.pricing, isDefault: false })}
                  >
                    <Copy size={15} aria-hidden="true" />
                  </button>
                )}
              </span>
            </div>
          </article>
          {/* Add a model: a card of its own at the end of the grid, + Add in the centre (Neil, 14 Oct 2026) */}
          {data.canEdit && data.models.length < 20 && (
            <button type="button" className="card pm-card pm-add" onClick={() => startNew("new")}>
              <span className="pm-add-label" title="A new pricing model, starting from our standard prices">
                <Plus size={20} aria-hidden="true" /> Add Pricing Model
              </span>
            </button>
          )}
        </div>
      ) : (
        <div className="pm-editor">
          <button type="button" className="pm-back" onClick={toList}>
            <ArrowLeft size={15} aria-hidden="true" /> All pricing models
          </button>
          <form className="pricing" onSubmit={save}>
            {/* The model's name, and whether it's pre-selected when ordering */}
            <section className="card pricing-name">
              {isStandard ? (
                <div className="pricing-std">
                  <div>
                    <h2>
                      {data.standard.name} <span className="pricing-readonly"><Lock size={11} aria-hidden="true" /> Read-only</span>
                    </h2>
                    <p className="card-sub">Used when you haven't made a pricing model of your own. Duplicate it to change any price or assumption.</p>
                  </div>
                  {data.canEdit && data.models.length < 20 && (
                    <button type="button" className="btn btn-primary" onClick={() => startNew("copy")}>
                      <Copy size={15} aria-hidden="true" /> Duplicate to edit
                    </button>
                  )}
                </div>
              ) : (
                <fieldset disabled={readOnly} className="pricing-main">
                  <Field label="Name" htmlFor="pr-name" hint="Shown when you choose pricing for a report, e.g. Schools, Large flat roofs">
                    <input id="pr-name" className="input" maxLength={60} value={sel.name} onChange={(e) => setSel({ ...sel, name: e.target.value })} />
                  </Field>
                  <Field label="Description" htmlFor="pr-desc" hint="What it's for, shown on its card, e.g. Flat-roofed schools with scaffold access">
                    <textarea
                      id="pr-desc"
                      className="input"
                      rows={2}
                      maxLength={200}
                      value={sel.description ?? ""}
                      onChange={(e) => setSel({ ...sel, description: e.target.value })}
                    />
                  </Field>
                  <Toggle
                    id="pr-default"
                    checked={sel.isDefault}
                    label="Pre-select this model when ordering"
                    onChange={(isDefault) => setSel({ ...sel, isDefault })}
                  />
                  {/* Its version: a new one on every change, so properties calculated on an older one say so */}
                  {!sel.isNew && sel.versionId && (
                    <p className="pricing-version muted">
                      <b>v{sel.version ?? 1}</b>
                      {sel.updatedAt
                        ? ` · last changed ${new Date(sel.updatedAt).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}`
                        : ""}
                      {sel.updatedBy ? ` by ${sel.updatedBy}` : ""}. Saving a change makes a new version: properties calculated on this one show a
                      warning and can be recalculated.
                    </p>
                  )}
                </fieldset>
              )}
            </section>
            <ReadOnlyCtx.Provider value={readOnly}>
            <fieldset disabled={readOnly} className="pricing-main">
              {/* What share of the roof is never planned for panels: the most important assumption */}
              <section className="card pr-clear">
                <h2>Roof left clear</h2>
                <Field
                  label="Share of the roof kept free of panels"
                  htmlFor="pr-clear"
                  hint="For edges, spacing, walkways, plant and rooflights, so the report never plans to cover the whole roof. Applied after the roof scan has fitted panels to the roof's shape. As a guide: about 15–25% on pitched roofs, 25–40% on flat roofs with tilted rows and walkways."
                >
                  <Num id="pr-clear" value={draft.roofLeftClearPct} onChange={(n) => update({ roofLeftClearPct: n })} suffix="%" />
                </Field>
              </section>

              {/* Installed price by system size: simple £/kWp, or built up from its parts */}
              <section className="card">
                <div className="pr-head">
                  <h2>Installed price</h2>
                  {readOnly ? (
                    draft.itemised && <span className="muted">Itemised</span>
                  ) : (
                    <Toggle id="pr-itemised" checked={draft.itemised} label="Itemise each price" onChange={setItemised} />
                  )}
                </div>
                <p className="card-sub">Price per kWp, excluding VAT, by system size: a system uses the first band it fits in.</p>
                <div className="pr-table-wrap">
                  <table className="pr-table">
                    <thead>
                      <tr>
                        <th>Systems up to</th>
                        {draft.itemised && PARTS.map(([, label]) => <th key={label}>{label}</th>)}
                        <th>Price per kWp</th>
                        {!readOnly && <th aria-label="Remove" />}
                      </tr>
                    </thead>
                    <tbody>
                      {draft.bands.map((b, i) => {
                        const total = draft.itemised && b.parts ? Object.values(b.parts).reduce((a, n) => a + (Number.isFinite(n) ? n : 0), 0) : b.perKwp;
                        return (
                          <tr key={i}>
                            <td>
                              <Num id={`pr-up-${i}`} value={b.upToKwp} onChange={(n) => setBand(i, { upToKwp: n })} suffix="kWp" />
                            </td>
                            {draft.itemised &&
                              PARTS.map(([k, label]) => (
                                <td key={k}>
                                  <Num
                                    id={`pr-${k}-${i}`}
                                    value={b.parts?.[k] ?? 0}
                                    onChange={(n) =>
                                      setBand(i, {
                                        parts: { ...(b.parts ?? { panels: 0, inverters: 0, mounting: 0, electrical: 0, labour: 0, margin: 0 }), [k]: n },
                                      })
                                    }
                                    prefix="£"
                                  />
                                  <span className="pr-sr">{label}</span>
                                </td>
                              ))}
                            <td>
                              {draft.itemised ? (
                                <b className="pr-total">{money(total)}</b>
                              ) : (
                                <Num id={`pr-per-${i}`} value={b.perKwp} onChange={(n) => setBand(i, { perKwp: n })} prefix="£" />
                              )}
                            </td>
                            {!readOnly && (
                            <td>
                              <button
                                type="button"
                                className="pr-remove"
                                aria-label="Remove this band"
                                disabled={draft.bands.length <= 1}
                                onClick={() => update({ bands: draft.bands.filter((_, j) => j !== i) })}
                              >
                                <Trash2 size={15} aria-hidden="true" />
                              </button>
                            </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {!readOnly && draft.bands.length < MAX_BANDS && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => {
                      const last = draft.bands[draft.bands.length - 1];
                      update({ bands: [...draft.bands, { ...last, upToKwp: (last?.upToKwp || 0) * 2 || 100 }] });
                    }}
                  >
                    <Plus size={14} aria-hidden="true" /> Add a size band
                  </button>
                )}
              </section>

              {/* Extras on top of the £/kWp */}
              <section className="card">
                <h2>Extras</h2>
                <Field label="Access" htmlFor="pr-access">
                  {readOnly ? (
                    <span className="pr-value">{ACCESS_LABELS[draft.access.mode] ?? draft.access.mode}</span>
                  ) : (
                  <Segmented
                    value={draft.access.mode}
                    options={[
                      ["scaffold", "Scaffolding"],
                      ["mewp", "Access platform"],
                      ["fixed", "Fixed price"],
                      ["none", "None"],
                    ]}
                    onChange={(mode) => update({ access: { ...draft.access, mode } })}
                  />
                  )}
                </Field>
                <div className="pr-grid">
                  {draft.access.mode === "scaffold" && (
                    <Field label="Scaffolding, per metre of roof edge" htmlFor="pr-scaf">
                      <Num
                        id="pr-scaf"
                        value={draft.access.scaffoldPerM}
                        onChange={(n) => update({ access: { ...draft.access, scaffoldPerM: n } })}
                        prefix="£"
                      />
                    </Field>
                  )}
                  {draft.access.mode === "mewp" && (
                    <>
                      <Field label="Access platform, per day" htmlFor="pr-mewp">
                        <Num id="pr-mewp" value={draft.access.mewpPerDay} onChange={(n) => update({ access: { ...draft.access, mewpPerDay: n } })} prefix="£" />
                      </Field>
                      <Field label="Installed per day" htmlFor="pr-kpd">
                        <Num id="pr-kpd" value={draft.access.kwpPerDay} onChange={(n) => update({ access: { ...draft.access, kwpPerDay: n } })} suffix="kWp" />
                      </Field>
                    </>
                  )}
                  {draft.access.mode === "fixed" && (
                    <Field label="Access, fixed price" htmlFor="pr-fixed">
                      <Num id="pr-fixed" value={draft.access.fixed} onChange={(n) => update({ access: { ...draft.access, fixed: n } })} prefix="£" />
                    </Field>
                  )}
                  <Field label="Structural survey" htmlFor="pr-survey">
                    <Num id="pr-survey" value={draft.structuralSurvey} onChange={(n) => update({ structuralSurvey: n })} prefix="£" />
                  </Field>
                  <Field label="Design" htmlFor="pr-design">
                    <Num id="pr-design" value={draft.designFee} onChange={(n) => update({ designFee: n })} prefix="£" />
                  </Field>
                  <Field label="G99 grid application" htmlFor="pr-g99">
                    <Num id="pr-g99" value={draft.g99Application} onChange={(n) => update({ g99Application: n })} prefix="£" />
                  </Field>
                  <Field label="G99 witness test" htmlFor="pr-witness" hint={`For systems over ${draft.g99WitnessKw} kW`}>
                    <Num id="pr-witness" value={draft.g99WitnessTest} onChange={(n) => update({ g99WitnessTest: n })} prefix="£" />
                  </Field>
                  <Field label="Witness test needed above" htmlFor="pr-witkw">
                    <Num id="pr-witkw" value={draft.g99WitnessKw} onChange={(n) => update({ g99WitnessKw: n })} suffix="kW" />
                  </Field>
                  <Field label="Monitoring" htmlFor="pr-mon">
                    <Num id="pr-mon" value={draft.monitoring} onChange={(n) => update({ monitoring: n })} prefix="£" />
                  </Field>
                </div>
              </section>

              {/* The rest of the calculation */}
              <section className="card">
                <h2>Assumptions</h2>
                <div className="pr-grid">
                  <Field label="Panel" htmlFor="pr-model">
                    {readOnly ? (
                      <span className="pr-value">{draft.panelModel || "—"}</span>
                    ) : (
                      <input id="pr-model" className="input" value={draft.panelModel} maxLength={60} onChange={(e) => update({ panelModel: e.target.value })} />
                    )}
                  </Field>
                  <Field label="Panel output" htmlFor="pr-watts">
                    <Num id="pr-watts" value={draft.panelWatts} onChange={(n) => update({ panelWatts: n })} suffix="W" />
                  </Field>
                  <Field label="Electricity price (the building's)" htmlFor="pr-import">
                    <Num id="pr-import" value={draft.importPence} onChange={(n) => update({ importPence: n })} step={0.1} suffix="p/kWh" />
                  </Field>
                  <Field label="Export price" htmlFor="pr-export">
                    <Num id="pr-export" value={draft.exportPence} onChange={(n) => update({ exportPence: n })} step={0.1} suffix="p/kWh" />
                  </Field>
                  <Field label="Electricity price rise" htmlFor="pr-infl">
                    <Num id="pr-infl" value={draft.priceInflationPct} onChange={(n) => update({ priceInflationPct: n })} step={0.1} suffix="% a year" />
                  </Field>
                  <Field label="System losses" htmlFor="pr-loss" hint="Inverter, cabling, soiling">
                    <Num id="pr-loss" value={draft.systemLossesPct} onChange={(n) => update({ systemLossesPct: n })} suffix="%" />
                  </Field>
                  <Field label="Maintenance and monitoring" htmlFor="pr-om">
                    <Num id="pr-om" value={draft.omPerKwp} onChange={(n) => update({ omPerKwp: n })} step={0.5} prefix="£" suffix="/kWp a year" />
                  </Field>
                  <Field label="VAT" htmlFor="pr-vat">
                    <Num id="pr-vat" value={draft.vatPct} onChange={(n) => update({ vatPct: n })} suffix="%" />
                  </Field>
                </div>
              </section>
            </fieldset>
            </ReadOnlyCtx.Provider>
            {!readOnly && (
              <div className="pricing-save">
                {!sel.isNew && (
                  <button type="button" className="btn btn-secondary" onClick={() => setToDelete({ id: sel.id, name: sel.name })} disabled={saving}>
                    <Trash2 size={15} aria-hidden="true" /> Delete
                  </button>
                )}
                {sel.isNew && (
                  <button type="button" className="btn btn-secondary" onClick={toList} disabled={saving}>
                    Cancel
                  </button>
                )}
                <button type="button" className="btn btn-secondary" onClick={() => update(structuredClone(data.standard.pricing))}>
                  <RotateCcw size={15} aria-hidden="true" /> Fill in our standard prices
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving || !sel.name.trim()}>
                  {saving ? "Saving…" : sel.isNew ? "Create pricing model" : "Save pricing model"}
                </button>
              </div>
            )}
            {sel.updatedAt && (
              <p className="muted pricing-updated">
                Last changed {new Date(sel.updatedAt).toLocaleString("en-GB", { dateStyle: "long", timeStyle: "short" })}
                {sel.updatedBy ? ` by ${sel.updatedBy}` : ""}.
              </p>
            )}
          </form>
        </div>
      )}

      <ConfirmDialog
        open={Boolean(toDelete)}
        title={`Delete "${toDelete?.name}"?`}
        confirmLabel="Delete pricing model"
        busy={saving}
        onConfirm={remove}
        onCancel={() => setToDelete(null)}
      >
        <p>It won't be offered for new reports. Reports already ordered with it keep its prices.</p>
      </ConfirmDialog>
    </div>
  );
}
